// =============================================
// 2. COMPLETE USER REPORT (FIXED)
// components/reports/UserReport.js
// =============================================

import React, { useMemo } from 'react'
import { Users, Clock, DollarSign, TrendingUp, Award } from 'lucide-react'
import { processTimeEntries } from '../../utils/timeSplitUtils'

export const UserReport = ({ data }) => {
  const userData = useMemo(() => {
    // Process time entries to handle splits
    const expandedEntries = processTimeEntries(data)
    const userMap = new Map()
    
    expandedEntries.forEach(entry => {
      const key = entry.user_id
      if (!userMap.has(key)) {
        userMap.set(key, {
          id: key,
          name: entry.user_name,
          totalHours: 0,
          billableHours: 0,
          revenue: 0,
          entries: 0,
          splitEntries: 0,
          clients: new Set(),
          projects: new Set()
        })
      }
      
      const user = userMap.get(key)
      user.totalHours += entry.duration_hours || 0
      user.entries += 1
      if (entry.is_split_entry) user.splitEntries += 1
      user.clients.add(entry.client_id)
      user.projects.add(entry.project_id)
      
      if (entry.is_billable) {
        user.billableHours += entry.duration_hours || 0
        user.revenue += (entry.duration_hours || 0) * (entry.hourly_rate || 0)
      }
    })
    
    return Array.from(userMap.values()).map(user => ({
      ...user,
      clientCount: user.clients.size,
      projectCount: user.projects.size,
      utilizationRate: user.totalHours > 0 ? (user.billableHours / user.totalHours) * 100 : 0,
      avgRate: user.billableHours > 0 ? user.revenue / user.billableHours : 0,
      originalEntries: user.entries - user.splitEntries
    })).sort((a, b) => b.revenue - a.revenue)
  }, [data])

  const topPerformer = userData[0]
  const totalRevenue = userData.reduce((sum, user) => sum + user.revenue, 0)

  if (!userData.length) {
    return (
      <div className="text-center py-12">
        <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
        <div className="text-slate-600">No user data found for the selected filters</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Top Performer Card */}
      {topPerformer && (
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl text-white p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Award className="w-5 h-5" />
                <span className="text-sm font-medium opacity-90">Top Performer</span>
              </div>
              <h3 className="text-2xl font-bold mb-1">{topPerformer.name}</h3>
              <div className="flex items-center space-x-4 text-sm opacity-90">
                <span>${topPerformer.revenue.toFixed(0)} cost</span>
                <span>{topPerformer.totalHours.toFixed(1)}h total</span>
                <span>{topPerformer.utilizationRate.toFixed(1)}% utilization</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">${topPerformer.avgRate.toFixed(0)}</div>
              <div className="text-sm opacity-90">avg rate/hr</div>
            </div>
          </div>
        </div>
      )}

      {/* User Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {userData.map((user, index) => (
          <div key={user.id} className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-slate-900">{user.name}</h3>
                <p className="text-sm text-slate-600">
                  {user.projectCount} projects • {user.clientCount} clients
                </p>
                {user.splitEntries > 0 && (
                  <p className="text-xs text-blue-600">
                    {user.splitEntries} split entries processed
                  </p>
                )}
              </div>
              {index === 0 && (
                <div className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
                  #1
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-slate-600">
                  <Clock className="w-4 h-4 mr-1" />
                  <span>Hours</span>
                </div>
                <div className="text-right">
                  <span className="font-medium text-slate-900">{user.totalHours.toFixed(1)}h</span>
                  <div className="text-xs text-slate-500">
                    {user.originalEntries} entries{user.splitEntries > 0 ? ` (+${user.splitEntries} splits)` : ''}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-slate-600">
                  <DollarSign className="w-4 h-4 mr-1" />
                  <span>Cost</span>
                </div>
                <div className="text-right">
                  <span className="font-medium text-green-600">${user.revenue.toFixed(0)}</span>
                  <div className="text-xs text-slate-500">
                    {totalRevenue > 0 ? ((user.revenue / totalRevenue) * 100).toFixed(1) : 0}% of total
                  </div>
                </div>
              </div>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center text-sm text-slate-600">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  <span>Rate</span>
                </div>
                <span className="font-medium text-slate-900">${user.avgRate.toFixed(0)}/h</span>
              </div>
              
              {/* Utilization Bar */}
              <div className="pt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-slate-600">Utilization</span>
                  <span className="text-xs font-medium text-slate-900">{user.utilizationRate.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      user.utilizationRate >= 80 ? 'bg-green-500' :
                      user.utilizationRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(user.utilizationRate, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h3 className="text-lg font-semibold text-slate-900">Team Performance Analysis</h3>
          <p className="text-sm text-slate-600 mt-1">Individual productivity metrics including time splits</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Team Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Projects
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Clients
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Total Hours
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Avg Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Utilization
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {userData.map((user, index) => (
                <tr key={user.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {index < 3 && (
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold mr-3">
                          {index + 1}
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-slate-900">{user.name}</div>
                        <div className="text-sm text-slate-500">
                          {user.originalEntries} entries{user.splitEntries > 0 ? ` (+${user.splitEntries} splits)` : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">{user.projectCount}</td>
                  <td className="px-6 py-4 text-sm text-slate-600">{user.clientCount}</td>
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">
                    {user.totalHours.toFixed(1)}h
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-green-600">
                    ${user.revenue.toFixed(0)}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-slate-900">
                    ${user.avgRate.toFixed(0)}/h
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-16 bg-slate-200 rounded-full h-2 mr-2">
                        <div 
                          className={`h-2 rounded-full ${
                            user.utilizationRate >= 80 ? 'bg-green-500' :
                            user.utilizationRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(user.utilizationRate, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-900">
                        {user.utilizationRate.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}


export default UserReport